/**
 * Audit Stream Hook
 *
 * Real-time polling hook for audit log updates.
 * Features auto-pause when user scrolls up.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiRequest } from "../../../api/client";

import type { AuditLogEntry, AuditLogsResponse, AuditFilters, LiveModeState } from "./types";
import { LIVE_MODE_INTERVAL } from "./types";

interface UseAuditStreamOptions {
  /** Whether live mode is enabled */
  readonly enabled: boolean;
  /** Current filters (excluding pagination) */
  readonly filters: AuditFilters;
  /** Callback when new entries arrive */
  readonly onNewEntries?: (entries: readonly AuditLogEntry[]) => void;
}

interface UseAuditStreamResult {
  /** Current live mode state */
  readonly liveState: LiveModeState;
  /** Start live mode */
  readonly start: () => void;
  /** Stop live mode */
  readonly stop: () => void;
  /** Pause live mode (when scrolling) */
  readonly pause: () => void;
  /** Resume live mode */
  readonly resume: () => void;
  /** New entries count since last view */
  readonly pendingCount: number;
  /** Clear pending count */
  readonly clearPending: () => void;
}

export function useAuditStream({
  enabled,
  filters,
  onNewEntries,
}: UseAuditStreamOptions): UseAuditStreamResult {
  const queryClient = useQueryClient();
  const [liveState, setLiveState] = useState<LiveModeState>({
    enabled: false,
    paused: false,
  });
  const [pendingCount, setPendingCount] = useState(0);
  const lastTimestampRef = useRef<string | null>(null);
  const onNewEntriesRef = useRef(onNewEntries);

  // Keep callback ref updated
  useEffect(() => {
    onNewEntriesRef.current = onNewEntries;
  }, [onNewEntries]);

  // Build query parameters
  const buildQueryParams = useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    params.set("limit", "20");

    // Map Studio filter names to API query parameter names
    if (filters.eventType && filters.eventType !== "all") {
      params.set("eventType", filters.eventType);
    }
    if (filters.environment && filters.environment !== "all") {
      params.set("environment", filters.environment);
    }
    if (filters.actorId) {
      params.set("actorId", filters.actorId);
    }
    if (filters.targetType && filters.targetType !== "all") {
      params.set("targetType", filters.targetType);
    }

    // Only fetch entries after last timestamp in live mode
    if (lastTimestampRef.current) {
      params.set("startDate", lastTimestampRef.current);
    }

    return params;
  }, [filters]);

  // Live mode polling query
  const { data: liveData } = useQuery({
    queryKey: ["audit-stream", filters, liveState.lastFetchTime],
    queryFn: async () => {
      const params = buildQueryParams();
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await apiRequest<AuditLogsResponse>(`/api/v1/admin/audit${query}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    enabled: enabled && liveState.enabled && !liveState.paused,
    refetchInterval: liveState.enabled && !liveState.paused ? LIVE_MODE_INTERVAL : false,
    staleTime: 0,
  });

  // Process new entries
  useEffect(() => {
    if (!liveData?.items.length) return;

    const newEntries = liveData.items.filter((entry) => {
      if (!lastTimestampRef.current) return true;
      return new Date(entry.timestamp) > new Date(lastTimestampRef.current);
    });

    const firstEntry = newEntries[0];
    if (newEntries.length > 0 && firstEntry) {
      // Update last timestamp
      const latestTimestamp = newEntries.reduce((latest, entry) => {
        return new Date(entry.timestamp) > new Date(latest) ? entry.timestamp : latest;
      }, firstEntry.timestamp);

      lastTimestampRef.current = latestTimestamp;

      // Update pending count if paused
      if (liveState.paused) {
        setPendingCount((prev) => prev + newEntries.length);
      }

      // Notify callback
      onNewEntriesRef.current?.(newEntries);

      // Invalidate main query to refresh data
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });

      // Update live state with last fetch time
      setLiveState((prev) => ({
        ...prev,
        lastFetchTime: new Date().toISOString(),
      }));
    }
  }, [liveData, liveState.paused, queryClient]);

  const start = useCallback((): void => {
    lastTimestampRef.current = new Date().toISOString();
    setPendingCount(0);
    setLiveState({
      enabled: true,
      paused: false,
      lastFetchTime: new Date().toISOString(),
    });
  }, []);

  const stop = useCallback((): void => {
    lastTimestampRef.current = null;
    setPendingCount(0);
    setLiveState({
      enabled: false,
      paused: false,
    });
  }, []);

  const pause = useCallback((): void => {
    setLiveState((prev) => ({
      ...prev,
      paused: true,
    }));
  }, []);

  const resume = useCallback((): void => {
    setPendingCount(0);
    setLiveState((prev) => ({
      ...prev,
      paused: false,
    }));
  }, []);

  const clearPending = useCallback((): void => {
    setPendingCount(0);
  }, []);

  return {
    liveState,
    start,
    stop,
    pause,
    resume,
    pendingCount,
    clearPending,
  };
}
